import React, { useState } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  Download, 
  FileText, 
  Image as ImageIcon, 
  Eye,
  X,
  Loader2
} from 'lucide-react';
import { formatDate } from '../services/api';

const MessageDisplay = ({ 
  message, 
  isOwnMessage, 
  senderName, 
  senderRole,
  showSenderInfo = true,
  onDownloadFile,
  currentUserRole = null, // Add current user role to determine display
  hideTimestamp = false // Add prop to hide timestamps
}) => {
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getRoleBadge = (role) => {
    const badges = {
      admin: { color: 'bg-red-600/20 text-red-300 border-red-500/30', label: 'Admin' },
      employee: { color: 'bg-blue-600/20 text-blue-300 border-blue-500/30', label: 'Employee' },
      customer: { color: 'bg-green-600/20 text-green-300 border-green-500/30', label: 'Customer' },
      demo: { color: 'bg-purple-600/20 text-purple-300 border-purple-500/30', label: 'Demo' },
    };
    return badges[role] || { color: 'bg-gray-600/20 text-gray-300 border-gray-500/30', label: 'User' };
  };

  // Determine display name based on current user role
  const getDisplayName = () => {
    if (isOwnMessage) return senderName; // Always show own name
    
    // If current user is customer or demo, show "BrixBot Support" for admin/employee messages
    if ((currentUserRole === 'customer' || currentUserRole === 'demo') && 
        (senderRole === 'admin' || senderRole === 'employee')) {
      return 'BrixBot Support';
    }
    
    // Otherwise show actual sender name
    return senderName || getRoleBadge(senderRole).label;
  };

  // Determine display role badge
  const getDisplayRoleBadge = () => {
    if (isOwnMessage) return getRoleBadge(senderRole);
    
    // If current user is customer or demo, show support badge for admin/employee messages
    if ((currentUserRole === 'customer' || currentUserRole === 'demo') && 
        (senderRole === 'admin' || senderRole === 'employee')) {
      return { color: 'bg-violet-600/20 text-violet-300 border-violet-500/30', label: 'BrixBot Support' };
    }
    
    // Otherwise show actual role badge
    return getRoleBadge(senderRole);
  };

  const handleDownload = async () => {
    if (message.attachment && onDownloadFile) {
      try {
        await onDownloadFile(message.attachment);
      } catch (error) {
        console.error('Download failed:', error);
      }
    }
  };

  const handleImagePreview = () => {
    if (message.attachment && message.attachment.content_type.startsWith('image/')) {
      setShowImagePreview(true);
    }
  };

  const isImage = message.attachment && message.attachment.content_type.startsWith('image/');
  const displayName = getDisplayName();
  const displayRoleBadge = getDisplayRoleBadge();

  return (
    <>
      <div className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'} mb-4`}>
        <div className={`max-w-xs lg:max-w-md ${isOwnMessage ? 'order-2' : ''}`}>
          {/* Sender Info */}
          {showSenderInfo && !isOwnMessage && (
            <div className="flex items-center space-x-2 mb-1">
              <div className={`w-6 h-6 rounded-full flex-shrink-0 ${
                (currentUserRole === 'customer' || currentUserRole === 'demo') && 
                (senderRole === 'admin' || senderRole === 'employee') 
                  ? 'bg-violet-500'  // BrixBot Support color
                  : senderRole === 'admin' ? 'bg-red-500' :
                    senderRole === 'employee' ? 'bg-blue-500' :
                    senderRole === 'customer' ? 'bg-green-500' :
                    'bg-purple-500'
              }`}></div>
              <Badge className={`${displayRoleBadge.color} text-xs`}>
                {displayName}
              </Badge>
              {!hideTimestamp && (
                <span className="text-gray-500 text-xs">
                  {formatDate(message.created_at || message.timestamp)}
                </span>
              )}
            </div>
          )}

          {/* Message Bubble */}
          <div className={`rounded-2xl shadow-lg ${
            isOwnMessage
              ? 'bg-violet-600 text-white'
              : 'bg-gray-800 text-gray-100 border border-gray-700'
          }`}>
            
            {/* File Attachment */}
            {message.attachment && (
              <div className={`p-3 ${message.content.trim() ? 'border-b border-gray-600/50' : ''}`}>
                {isImage ? (
                  /* Image Attachment */
                  <div className="space-y-2">
                    <div 
                      className="relative cursor-pointer group rounded-lg overflow-hidden"
                      onClick={handleImagePreview}
                    >
                      {imageLoading && (
                        <div className="flex items-center justify-center bg-gray-800/50 rounded-lg h-32">
                          <Loader2 className="w-6 h-6 animate-spin text-white" />
                        </div>
                      )}
                      <img
                        src={`data:${message.attachment.content_type};base64,${message.attachment.file_data}`}
                        alt={message.attachment.filename}
                        className="max-w-full h-auto max-h-64 rounded-lg"
                        onLoad={() => setImageLoading(false)}
                        onError={(e) => {
                          console.error('Image load error:', e);
                          setImageLoading(false);
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'block';
                        }}
                        style={{display: imageLoading ? 'none' : 'block'}}
                      />
                      <div className="hidden bg-gray-800 p-4 rounded-lg text-center">
                        <FileText className="w-8 h-8 text-red-400 mx-auto mb-2" />
                        <p className="text-white text-sm">Image failed to load</p>
                        <p className="text-gray-400 text-xs">{message.attachment.filename}</p>
                      </div>
                      <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <Eye className="w-6 h-6 text-white" />
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <ImageIcon className="w-4 h-4" />
                        <div>
                          <p className="text-sm font-medium">{message.attachment.filename}</p>
                          <p className="text-xs opacity-75">{formatFileSize(message.attachment.file_size)}</p>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={handleDownload}
                        className={`${isOwnMessage ? 'hover:bg-white/20' : 'hover:bg-gray-700'}`}
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  /* File Attachment */
                  <div className="flex items-center justify-between p-2 bg-gray-700/50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <FileText className="w-8 h-8 text-blue-400 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{message.attachment.filename}</p>
                        <p className="text-xs opacity-75">{formatFileSize(message.attachment.file_size)}</p>
                        <p className="text-xs opacity-60">{message.attachment.content_type}</p>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={handleDownload}
                      className={`${isOwnMessage ? 'hover:bg-white/20' : 'hover:bg-gray-700'} flex-shrink-0`}
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Text Content */}
            {message.content.trim() && (
              <div className="p-3">
                <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">
                  {message.content}
                </p>
              </div>
            )}

            {/* Timestamp for own messages */}
            {isOwnMessage && !hideTimestamp && (
              <div className="px-3 pb-2">
                <span className="text-xs opacity-75">
                  {formatDate(message.created_at || message.timestamp)}
                </span>
                {message.sending && (
                  <span className="text-xs opacity-75 ml-2">• Sending...</span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Image Preview Modal */}
      {showImagePreview && isImage && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="relative max-w-4xl max-h-4xl">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowImagePreview(false)}
              className="absolute -top-12 right-0 text-white hover:text-gray-300"
            >
              <X className="w-6 h-6" />
            </Button>
            <img
              src={`data:${message.attachment.content_type};base64,${message.attachment.file_data}`}
              alt={message.attachment.filename}
              className="max-w-full max-h-full rounded-lg"
            />
            <div className="absolute -bottom-12 left-0 text-white">
              <p className="text-sm font-medium">{message.attachment.filename}</p>
              <p className="text-xs opacity-75">{formatFileSize(message.attachment.file_size)}</p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MessageDisplay;